import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { useChargeLeasingFee } from "@/hooks/api/useContractBilling";
import { useUpdateContract } from "@/hooks/api/useContracts";
import { money } from "@/lib/money";
import type { components } from "@/types/api";

type Contract = components["schemas"]["ContractResponse"];

/** The one-off leasing fee billed to the landlord when a tenant is placed.
 *  Set the amount, then charge it as a landlord invoice (nets from their payout). */
export function LeasingFeeCard({ contract }: { contract: Contract }) {
  const update = useUpdateContract(contract.id);
  const charge = useChargeLeasingFee(contract.id);
  const [fee, setFee] = useState(String(contract.leasing_fee ?? 0));

  const hasLandlord = Boolean(contract.landlord_id);
  const feeNum = Number(fee) || 0;

  const onSave = () =>
    update.mutate({ leasing_fee: feeNum } as Record<string, unknown>, {
      onSuccess: () => toast.success("Leasing fee saved"),
      onError: (e) => toast.error(e.message),
    });

  const onCharge = () =>
    charge.mutate(undefined, {
      onSuccess: (inv) => toast.success(`Leasing fee invoiced to the landlord (${inv.number})`),
      onError: (e) => toast.error(e.message),
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Leasing fee (one-off)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The placement fee charged to the landlord. Charging it creates a landlord
          invoice (incl. {((contract.leasing_fee_vat_rate ?? 0) * 100).toFixed(0)}% VAT) that can
          be netted from their payouts.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="leasing-fee">Amount (€)</Label>
            <Input
              id="leasing-fee"
              type="number"
              step="1"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              className="h-9 w-32"
            />
          </div>
          <Button size="sm" variant="outline" disabled={update.isPending} onClick={onSave}>
            Save
          </Button>
          <Button
            size="sm"
            disabled={!hasLandlord || feeNum <= 0 || charge.isPending}
            onClick={onCharge}
          >
            {charge.isPending ? "Charging…" : `Charge ${money(feeNum)} to landlord`}
          </Button>
        </div>
        {!hasLandlord && (
          <p className="text-xs text-muted-foreground">
            Link a landlord to the contract to charge the leasing fee.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
